import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Header, { serviceName } from './components/Header';
import Navigation from './components/Navigation';
import SearchPage from './pages/SearchPage';
import SettingsPage from './pages/SettingsPage';
import ProvidersPage from './pages/ProvidersPage';
import KnowledgePage from './pages/KnowledgePage';
import AIChatTestPage from './pages/AIChatTestPage';
import TicketsPage from './pages/TicketsPage';
import LearningPage from './pages/LearningPage';
import PromptPage from './pages/PromptPage';
import { ShieldX } from 'lucide-react';
import { withAuthHeaders, startTelegramOidcLogin, consumeTelegramOidcRedirect } from './utils/auth';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGIN_STORAGE_KEY = 'support_tg_login_data';

function AccessDenied() {
  return (
    <div className="access-denied" data-testid="access-denied">
      <div className="access-denied-icon">
        <ShieldX size={48} />
      </div>
      <h2>Доступ запрещён</h2>
      <p>Mini App доступен только для менеджеров.</p>
      <p className="text-muted">Если вы менеджер — обратитесь к администратору для добавления вашего ID.</p>
    </div>
  );
}

function TelegramLogin({ clientId, onLoginClick, loading, error }) {
  return (
    <div className="login-screen" data-testid="telegram-login">
      <div className="login-panel">
        <div className="login-icon"><ShieldX size={34} /></div>
        <h2>Вход для менеджеров</h2>
        <p>Авторизуйтесь через Telegram, чтобы открыть панель поддержки.</p>
        {clientId ? (
          <button className="btn btn-primary" onClick={onLoginClick} disabled={loading} data-testid="telegram-oidc-login-btn">
            {loading ? 'Проверка входа...' : 'Войти через Telegram'}
          </button>
        ) : (
          <div className="alert alert-error">Не удалось получить конфигурацию входа.</div>
        )}
        {error && <div className="alert alert-error">{error}</div>}
      </div>
    </div>
  );
}

function App() {
  const [page, setPage] = useState('search');
  const [settings, setSettings] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [telegramUser, setTelegramUser] = useState(null);
  const [auth, setAuth] = useState(null);
  const [authConfig, setAuthConfig] = useState(null);
  const [loginRequired, setLoginRequired] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [aiTab, setAiTab] = useState('providers');

  // Сохранение состояния поиска между вкладками
  const [searchState, setSearchState] = useState({
    query: '',
    result: null,
    section: 'profile'
  });

  // Заголовок вкладки — по названию сервиса из настроек
  useEffect(() => {
    document.title = `${serviceName(settings || authConfig)} — поддержка`;
  }, [settings, authConfig]);

  const fetchAuthConfig = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/auth/config`);
      const data = await r.json();
      setAuthConfig(data);
      return data;
    } catch (e) {
      console.error('Auth config fetch error:', e);
      return null;
    }
  }, []);

  const fetchSettings = useCallback(async (authData) => {
    try {
      const headers = withAuthHeaders(authData);
      const r = await fetch(`${API}/api/settings`, { headers });
      if (r.status === 401 || r.status === 403) return null;
      const data = await r.json();
      setSettings(data);
      return data;
    } catch (e) {
      console.error('Settings fetch error:', e);
      return null;
    }
  }, []);

  const fetchProviders = useCallback(async (authData) => {
    try {
      const headers = withAuthHeaders(authData);
      const r = await fetch(`${API}/api/settings/providers`, { headers });
      if (r.status === 401 || r.status === 403) return;
      const data = await r.json();
      setProviders(data.providers || []);
    } catch (e) {
      console.error('Providers fetch error:', e);
    }
  }, []);

  const fetchMe = useCallback(async (authData) => {
    try {
      const r = await fetch(`${API}/api/auth/me`, { headers: withAuthHeaders(authData) });
      if (!r.ok) return null;
      return await r.json();
    } catch (e) {
      console.error('Me fetch error:', e);
      return null;
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      let tgUser = null;
      let authData = null;

      if (typeof window.Telegram !== 'undefined' && window.Telegram.WebApp) {
        window.Telegram.WebApp.ready();
        window.Telegram.WebApp.expand();

        const rawData = window.Telegram.WebApp.initData;
        if (rawData) {
          authData = { type: 'webapp', value: rawData };
          setAuth(authData);
        }

        const initDataUnsafe = window.Telegram.WebApp.initDataUnsafe;
        if (initDataUnsafe?.user) {
          tgUser = initDataUnsafe.user;
          setTelegramUser(tgUser);
        }
      }

      if (!authData) {
        await fetchAuthConfig();

        const oidcRedirect = consumeTelegramOidcRedirect();
        if (oidcRedirect) {
          try {
            const r = await fetch(`${API}/api/auth/telegram/callback`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                code: oidcRedirect.code,
                code_verifier: oidcRedirect.codeVerifier,
                redirect_uri: window.location.origin + window.location.pathname,
              }),
            });
            const data = await r.json();
            if (data.ok && data.login_data) {
              localStorage.setItem(LOGIN_STORAGE_KEY, data.login_data);
              authData = { type: 'login', value: data.login_data };
              setAuth(authData);
              tgUser = data.user;
              setTelegramUser(tgUser);
            } else {
              setLoginError('Telegram не подтвердил вход. Попробуйте ещё раз.');
            }
          } catch (e) {
            console.error('OIDC callback error:', e);
            setLoginError('Ошибка при входе через Telegram. Попробуйте ещё раз.');
          }
        }

        if (!authData) {
          const savedLoginData = localStorage.getItem(LOGIN_STORAGE_KEY);
          if (savedLoginData) {
            authData = { type: 'login', value: savedLoginData };
            setAuth(authData);
            const params = new URLSearchParams(savedLoginData);
            tgUser = {
              id: Number(params.get('id')),
              first_name: params.get('first_name') || '',
              last_name: params.get('last_name') || '',
              username: params.get('username') || '',
              photo_url: params.get('photo_url') || '',
            };
            setTelegramUser(tgUser);
          }
        }
      }

      if (!authData) {
        setLoginRequired(true);
        setLoading(false);
        return;
      }

      const settingsData = await fetchSettings(authData);
      const me = settingsData ? await fetchMe(authData) : null;
      const admin = !!me?.is_admin;
      setIsAdmin(admin);
      if (admin) await fetchProviders(authData);

      if (!settingsData) {
        if (authData.type === 'login') localStorage.removeItem(LOGIN_STORAGE_KEY);
        setAuth(null);
        setTelegramUser(null);
        setLoginRequired(true);
        setLoginError('Сессия Telegram недействительна или истекла. Войдите заново.');
        setLoading(false);
        return;
      }

      // Бэкенд уже проверил, что это менеджер (иначе settings вернул бы 403)
      setAccessDenied(!me?.id);

      setLoading(false);
    };

    init();
  }, [fetchSettings, fetchProviders, fetchAuthConfig, fetchMe]);

  const handleLoginClick = useCallback(() => {
    if (!authConfig?.client_id) return;
    setLoginError('');
    startTelegramOidcLogin({
      clientId: authConfig.client_id,
      redirectUri: window.location.origin + window.location.pathname,
    });
  }, [authConfig]);

  const logout = useCallback(() => {
    localStorage.removeItem(LOGIN_STORAGE_KEY);
    setAuth(null);
    setTelegramUser(null);
    setSettings(null);
    setProviders([]);
    setAccessDenied(false);
    setIsAdmin(false);
    setLoginRequired(true);
  }, []);

  if (loading) {
    return (
      <div className="app-loading" data-testid="app-loading">
        <div className="loading-spinner" />
        <span>Загрузка...</span>
      </div>
    );
  }

  if (loginRequired) {
    return (
      <div className="app" data-testid="app-container">
        <Header settings={settings || authConfig} />
        <TelegramLogin clientId={authConfig?.client_id} onLoginClick={handleLoginClick} loading={loading} error={loginError} />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="app" data-testid="app-container">
        <Header settings={settings} telegramUser={telegramUser} onLogout={logout} />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div className="app" data-testid="app-container">
      <Header settings={settings} telegramUser={telegramUser} onLogout={logout} />
      <Navigation page={page} setPage={setPage} isAdmin={isAdmin} />
      <main className="app-main">
        {/* Поиск — сохраняет состояние */}
        <div style={{ display: page === 'search' ? 'block' : 'none' }}>
          <SearchPage
            settings={settings}
            searchState={searchState}
            setSearchState={setSearchState}
            auth={auth}
          />
        </div>

        {/* Тикеты */}
        <div style={{ display: page === 'tickets' ? 'block' : 'none' }}>
          <TicketsPage settings={settings} auth={auth} />
        </div>

        {/* AI Чат */}
        {isAdmin && page === 'chat-test' && <AIChatTestPage settings={settings} auth={auth} />}

        {/* AI Провайдеры */}
        {isAdmin && page === 'providers' && (
          <>
            <div className="filter-tabs" style={{ marginBottom: 12 }}>
              <button className={`filter-tab ${aiTab === 'providers' ? 'active' : ''}`} onClick={() => setAiTab('providers')}>Провайдеры</button>
              <button className={`filter-tab ${aiTab === 'prompt' ? 'active' : ''}`} onClick={() => setAiTab('prompt')}>Промпт</button>
            </div>
            {aiTab === 'providers' ? (
              <ProvidersPage
                providers={providers}
                settings={settings}
                auth={auth}
                onRefresh={() => { fetchProviders(auth); fetchSettings(auth); }}
              />
            ) : (
              <PromptPage auth={auth} onOpen={setPage} />
            )}
          </>
        )}

        {/* Аналитика и самообучение AI (только админ) */}
        {isAdmin && page === 'learning' && <LearningPage auth={auth} />}

        {/* База знаний */}
        {page === 'knowledge' && <KnowledgePage auth={auth} isAdmin={isAdmin} />}

        {/* Настройки */}
        {isAdmin && page === 'settings' && (
          <SettingsPage
            settings={settings}
            auth={auth}
            onUpdate={() => fetchSettings(auth)}
          />
        )}
      </main>
    </div>
  );
}

export default App;
